import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { CreateContractOffer, getExpressionBid } from '../../services/procurementService';
import { TenderDocumentType } from '../../types/enums';
import { useEffect, useState } from 'react';
import ConfirmationModal from '../../components/ConfirmModal/ConfirmationModal';
import { getProcuremtDocuments, viewDocuments } from '../../services/edmsService';
import { getUploadedDocName } from '../../utils/helper';
import { useTranslation } from 'react-i18next';
import { BidApplicantsResponse } from '../../types/procurement-types';

interface CoordinateDMSProps {
  value: number;
  type: 'Latitude' | 'Longitude';
}

function CoordinateDMS({ value, type }: CoordinateDMSProps) {
  const { t } = useTranslation();

  if (value === undefined || value === null) return null;

  const toDMS = (deg: number, isLatitude: boolean): string => {
    const absolute = Math.abs(deg);
    const degrees = Math.floor(absolute);
    const minutesNotTruncated = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesNotTruncated);
    const seconds = ((minutesNotTruncated - minutes) * 60).toFixed(2);

    const direction = isLatitude ? (deg >= 0 ? 'N' : 'S') : deg >= 0 ? 'E' : 'W';

    return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
  };

  return (
    <span>
      {t(type.toLowerCase(), type)}: {toDMS(value, type === 'Latitude')}
    </span>
  );
}

const ExpressionOfInterestProposalPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenderBidId, serviceInfoId } = useParams<{
    tenderBidId: string;
    serviceInfoId: string;
  }>();

  const [modalOpen, setModalOpen] = useState(false);
  const [bidInfo, setBidInfo] = useState<BidApplicantsResponse>();
  const [actionStatus, setActionStatus] = useState<'Approved' | 'Denied' | null>(null);

  const { data: expressionBidResponse } = useQuery({
    queryKey: ['bids-expression', tenderBidId],
    queryFn: async () => {
      try {
        const response = await getExpressionBid(tenderBidId as string);
        if (response.isSuccess) return response?.data || [];
      } catch (error) {
        toast.error(
          t('failedToFetchExpressionBid', 'Failed to fetch expression bid. Please try again later.')
        );
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const { data: expressionBidDocument } = useQuery({
    queryKey: ['bids-expression-document', tenderBidId],
    queryFn: async () => {
      try {
        const response = await getProcuremtDocuments({
          entityId: tenderBidId,
          subModule: TenderDocumentType.EXPRESSION_OF_INTEREST,
        });

        if (response.isSuccess) return response?.data || [];
      } catch (error) {
        toast.error(
          t('failedToFetchDocuments', 'Failed to fetch documents. Please try again later.')
        );
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const approveOrRejectMutation = useMutation({
    mutationFn: (status: 'Approved' | 'Denied') =>
      CreateContractOffer({
        tenderType: TenderDocumentType.EXPRESSION_OF_INTEREST,
        status: status,
        tenderBidId: tenderBidId as string,
      }),
    onSuccess: (response) => {
      if (response.isSuccess) {
        toast.success(response.data.message);
        navigate(`/procurement/proposals/${serviceInfoId}?tab=allBids`, { replace: true });
      } else {
        toast.error(response.errorMessage);
      }
    },
    onError: () => {
      toast.error(t('actionFailed', 'Unable to perform action, please try again later'));
    },
  });

  useEffect(() => {
    if (location.state && location.state.bidItem) {
      const { bidItem } = location.state;
      setBidInfo(bidItem);
    }
  }, [location.state]);

  const handleAction = (status: 'Approved' | 'Denied') => {
    setActionStatus(status);
    setModalOpen(true);
  };

  const confirmAction = () => {
    if (actionStatus) {
      approveOrRejectMutation.mutate(actionStatus);
      setModalOpen(false);
    }
  };

  const handleFetchDocuments = async (selectedDocumentId: string, docName: string) => {
    try {
      const response = await viewDocuments({
        documentId: selectedDocumentId,
        module: 'procurement',
      });

      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${docName}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('An error occurred:', error);
      toast.error(t('actionFailed', 'An error occurred. Please try again.'));
    }
  };

  const actionText = actionStatus === 'Approved' ? t('approve', 'approve') : t('deny', 'deny');

  const message = t('confirmActionMessage', 'Are you sure you want to {action} this bid?').replace(
    '{action}',
    actionText
  );

  return (
    <div className="mx-auto max-w-screen-xl p-8">
      <h1 className="mb-1 text-2xl font-semibold text-gray-900">
        {t('expressionOfInterest', 'Expression of Interest')}
      </h1>

      <div className="2xl:w-11/12">
        <div className="mb-2.5 mt-8 flex flex-col flex-wrap justify-between gap-x-7 gap-y-4 rounded-xl border border-[#E5E5E5] bg-white p-5 pb-12 xl:flex-row xl:items-center">
          <div className="flex w-full flex-wrap justify-between gap-3">
            <div className="flex items-center gap-x-6">
              <img
                src="https://placehold.co/400"
                alt="vendor images"
                className="h-[58px] w-[58px] rounded-full"
              />

              <div className="flex flex-col items-start gap-y-2">
                <p className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-[#101828] underline">
                  {bidInfo?.applicantName}
                </p>

                <span className="rounded-[30px] bg-[#42BE65] px-2.5 py-1 text-[10px] font-medium text-white">
                  {t('verifiedSupplier', 'Verified Supplier')}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="text-sm text-[#8D8D8D]">
                {t('tenderOpportunity', 'Tender Opportunity')}
              </span>
              <span className="text-sm font-medium text-[#525252]">
                Pipeline Inspection and Maintenance
              </span>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="text-sm text-[#8D8D8D]">{t('type', 'Type')}</span>
              <span className="text-sm font-medium text-[#525252]">Joint Venture</span>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="text-sm text-[#8D8D8D]">{t('submitted', 'Submitted')}</span>
              <span className="text-sm font-medium text-[#525252]">
                {moment(bidInfo?.submissionDate).format('DD/MM/YYYY')}
              </span>
            </div>
          </div>

          <button className="ml-auto flex h-12 w-[140px] items-center justify-center rounded-lg border border-[#D0D5DD] px-3.5 py-2 text-sm font-semibold text-[#344054] shadow-sm">
            {t('viewDetails', 'View Details')}
          </button>
        </div>

        <div className="my-4 rounded-lg bg-[#24A148] px-5">
          <p className="text-xl font-semibold text-white">{t('stage1Of3', 'Stage 1 of 3')}</p>
        </div>

        <div className="mb-4 rounded-2xl border border-[#E5E5E5] bg-white pb-[54px] pl-[34px] pr-[26px] pt-[33px]">
          <h3 className="mb-[34px] text-xl font-medium text-[#101828]">
            {t('basicInformation', 'Basic information')}
          </h3>

          <div className="mx-auto grid w-3/4 grid-cols-2 gap-x-[10%] gap-y-4">
            <div className="flex flex-col gap-y-4">
              <span className="text-sm font-medium text-[#8D8D8D]">{t('address', 'Address')}</span>
              <span className="pb-2.5 text-sm font-medium text-[#101828]">
                {expressionBidResponse?.address}
              </span>
            </div>

            <div className="flex flex-col gap-y-4">
              <span className="text-sm font-medium text-[#8D8D8D]">
                {t('location', 'Location')}
              </span>
              <span className="pb-2.5 text-sm font-medium text-[#101828]">
                {expressionBidResponse?.location}
              </span>
            </div>

            <div className="flex flex-col gap-y-4">
              <span className="text-sm font-medium text-[#8D8D8D]">{t('state', 'State')}</span>
              <span className="pb-2.5 text-sm font-medium text-[#101828]">
                {expressionBidResponse?.state}
              </span>
            </div>

            <div className="flex flex-col gap-y-4">
              <span className="text-sm font-medium text-[#8D8D8D]">
                {t('coordinates', 'Coordinates')}
              </span>
              <span className="pb-2.5 text-sm font-medium text-[#101828]">
                {CoordinateDMS({
                  value: expressionBidResponse?.longitude as number,
                  type: 'Longitude',
                })}
                ,{' '}
                {CoordinateDMS({
                  value: expressionBidResponse?.latitude as number,
                  type: 'Latitude',
                })}
              </span>
            </div>
          </div>
        </div>

        <div className="mb-4 rounded-2xl border border-[#E5E5E5] bg-white pb-[54px] pl-[34px] pr-[26px] pt-[33px]">
          <div className="mb-6">
            <h3 className="mb-[34px] text-xl font-medium text-[#101828]">
              {t('projectTimeline', 'Project Timeline')}
            </h3>

            <div className="mx-auto grid w-3/4 grid-cols-2 gap-x-[10%] gap-y-4">
              <div className="flex flex-col gap-y-4">
                <span className="text-sm font-medium text-[#8D8D8D]">
                  {t('estimatedCostOfProject', 'Estimated Cost of Project')}
                </span>
                <span className="pb-2.5 text-[32px] font-semibold text-[#24A148]">
                  $
                  {expressionBidResponse?.estimatedCost
                    ? new Intl.NumberFormat('en-US').format(expressionBidResponse?.estimatedCost)
                    : ''}
                </span>
              </div>
              <div></div>

              <div className="flex flex-col gap-y-4">
                <span className="text-sm font-medium text-[#8D8D8D]">
                  {t('estimatedStartDate', 'Estimated Start Date')}
                </span>
                <div className="flex items-center gap-x-2 pb-2.5">
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/icons/outline/calendar.svg`}
                    alt="calendar icon"
                  />
                  <span className="text-sm font-medium text-[#101828]">
                    {moment(expressionBidResponse?.estimatedStartDate).format('DD/MM/YYYY')}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-y-4">
                <span className="text-sm font-medium text-[#8D8D8D]">
                  {t('estimatedEndDate', 'Estimated End Date')}
                </span>
                <div className="flex items-center gap-x-2 pb-2.5">
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/icons/outline/calendar.svg`}
                    alt="calendar icon"
                  />
                  <span className="text-sm font-medium text-[#101828]">
                    {moment(expressionBidResponse?.estimatedEndDate).format('DD/MM/YYYY')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="">
            <h3 className="mb-[34px] text-xl font-medium text-[#101828]">
              {t('requiredDocuments', 'Required Documents')}
            </h3>

            <div className="mx-auto w-3/4 gap-y-4">
              {expressionBidDocument?.map((doc: any) => {
                const docName = getUploadedDocName(doc.documentName);
                return (
                  <div key={doc.tenderBidDocumentId} className="mb-6 flex flex-col gap-y-4">
                    <span className="text-sm font-medium text-[#8D8D8D]">
                      {doc?.documentTypeName || t('requiredDocName', 'Required Doc Name')}
                    </span>

                    <div
                      className="flex items-center gap-x-[30px] rounded-[5px] bg-[#F5F5F5] py-2.5 text-sm text-[#667085]"
                      role="button"
                      onClick={() => handleFetchDocuments(doc?.tenderBidDocumentId, docName)}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/assets/images/dashicons_pdf.svg`}
                        alt="pdf icon"
                      />
                      {docName}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mb-[45px] rounded-2xl border border-[#E5E5E5] bg-white pb-[54px] pl-[34px] pr-[26px] pt-[33px]">
          <h3 className="mb-5 text-lg font-medium text-black">
            {t('coverLetter', 'Cover Letter')}
          </h3>

          <p dangerouslySetInnerHTML={{ __html: expressionBidResponse?.purpose as string }} />
        </div>

        <div className="grid grid-cols-2 gap-x-[32px]">
          <button
            className="rounded-lg border border-[#FA4D56] px-5 py-3 text-sm font-semibold text-[#FA4D56]"
            onClick={() => handleAction('Denied')}
            disabled={approveOrRejectMutation.isPending}
          >
            {t('reject', 'Reject')}
          </button>

          <button
            className="rounded-lg border border-[#24A148] bg-[#24A148] px-5 py-3 text-sm font-semibold text-white"
            onClick={() => handleAction('Approved')}
            disabled={approveOrRejectMutation.isPending}
          >
            {t('approveForNextStage', 'Approve for Next Stage')}
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={confirmAction}
        title={t('confirmAction', 'Confirm Action')}
        message={message}
      />
    </div>
  );
};

export default ExpressionOfInterestProposalPage;
